'use client';

import { DisableInput, DisregardEventResponse } from '@/app/models/disregardEvents';
import { useConsiderAllMutation, useListAllEventsQuery } from '@/app/services/disregardEventsApi';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import PageTitle from '@/components/common/pageTitle';
import TextButton from '@/components/common/TextButton';
import DisregardEventsTable from '@/components/disregardEvents/DisregardEventsTable';
import NewDisregardEvents from '@/components/disregardEvents/NewDisregardEvents';
import PeriodDetails from '@/components/periodDetails/PeriodDetails';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

function DisregardEvents() {
  const theme = useTheme();
  const { t } = useTranslation('disregardEvents');
  const [drawerStatus, setDrawerStatus] = useState(false);
  const [selectedCollaborators, setSelectedCollaborators] = useState<string[]>([]);

  const [event, setEvent] = useState<DisregardEventResponse | null>(null);
  const [eventSearch, setEventSearch] = useState('');
  const { data: events, isFetching: isLoadingevents } = useListAllEventsQuery(
    {
      searchTerm: eventSearch,
      bringExcluded: true,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: eventSearch.length < 1 },
  );

  const [enableAll] = useConsiderAllMutation();

  const handleSubmit = async () => {
    if (!selectedCollaborators || selectedCollaborators.length < 1) return;
    const requestBody: DisableInput = {
      codes: selectedCollaborators,
    };
    await toast.promise(
      enableAll(requestBody),
      {
        pending: t('toast.consider.events.sending') as string,
        success: t('toast.consider.events.success') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setSelectedCollaborators([]);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <PeriodDetails />
      <PageTitle
        title={t('remove.event.title')}
        titleLabel={` (${t('remove.event.salary')})`}
        subtitle={`${t('remove.event.subtitle')}`}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'end',
        }}
      >
        <Box sx={{ flex: 1, minWidth: '250px' }}>
          <Typography component="p" variant="fieldHeader">
            {t('event')}
          </Typography>
          <GenericAutoComplete
            value={event}
            onChange={(value) => {
              setEvent(value);
            }}
            equalityTest={(option, value) => option?.name === value?.name}
            getOptionLabel={(option) => `${option?.code} - ${option?.name}`}
            onSearch={(value) => {
              setEventSearch(value);
            }}
            options={eventSearch !== '' && events?.content ? events?.content : []}
            placeholder={t('event.placeholder') || ''}
            loading={isLoadingevents}
            sx={{ width: theme.spacing(50) }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Button
            variant="primary"
            disabled={selectedCollaborators.length < 1}
            onClick={() => {
              handleSubmit();
            }}
            sx={{
              textTransform: 'none',
            }}
          >
            {t('button.consider.events')}
          </Button>
          <TextButton
            text={`+ ${t('button.remove.event')}`}
            onClick={() => {
              setDrawerStatus(true);
            }}
          />
        </Box>
      </Box>

      <DisregardEventsTable
        selectedCollaborators={selectedCollaborators}
        setSelectedCollaborators={setSelectedCollaborators}
        searchTerm={event?.name || ''}
      />

      <LateralDrawer open={drawerStatus} setOpen={setDrawerStatus} position="right" size="bigger">
        <NewDisregardEvents setDrawerOpen={setDrawerStatus} />
      </LateralDrawer>
    </Box>
  );
}

export default DisregardEvents;
